import { DataService } from "@/services/data";
import { readsFormValues, useDynamicOptions } from "@/hooks/use-dynamic-options";
import { buildZodSchema, buildDefaultValues, errorText, formValueOf, normalizeJsonFieldValues } from "@/utils/dynamic-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { errorsElsewhere } from "@/utils/step-errors";
import { Form } from "../ui/form";
import { FormError } from "../ui/form-error";
import { DynamicFormField, FieldGroupBlock, fieldRuns } from "./dynamic-form-field";
import { resetFieldMemory } from "@/utils/field-memory";
import { AlertLayout } from "./alerts";
import type { FieldDataType, FormAlertType, FormConfigType, FormStepDescription, FormStepGauge, FormStepLiveResponse, FormStepResponse, FormStepSummary } from "@/types/dynamic-detail";
import { bugsnagNotify } from "@/utils/bugsnag-notify";
import { hasFormConditions, keepVisibleValues, optionAttributeReader, previewKeyOf, resolveConditionalFields } from "@/utils/form-conditions";
import { currentDescriptors, effectiveOptionMap } from "@/utils/field-options";
import { buildFormDraftKey, useFormDraft } from "@/hooks/use-form-draft";
import { useUser } from "@/providers/user";
import { FormDraftNotice } from "./form-draft-notice";
import { FormStepper, FormSummaryPanel, FormSummaryReview } from "./form-steps";
import { dialogClass } from "./dialog-size";

type FormHandlerPayload = { values?: Record<string, any>; alerts?: FormAlertType[] };

/** Debounce of `form-preview` calls while the user types (G3). */
const PREVIEW_DEBOUNCE_MS = 400;

/** Record a rejected write collided with, reported by a uniqueness validator. */
type DuplicateRecord = {
    field: string;
    id: number | string;
    label: string;
    url: string | null;
};

type ValidationErrorPayload = {
    /** A sentence; older answers sent an object of messages per field: never rendered as such. */
    error?: string | Record<string, string[] | string>;
    field_errors?: Record<string, string[]>;
    duplicate?: DuplicateRecord;
};

type DynamicObjectFormProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: "create" | "update";
    formConfig: FormConfigType;
    endpointUrl: string;
    /** Record id for update mode */
    recordId?: string | number;
    /** Initial values for pre-filling form (update mode) */
    initialValues?: Record<string, any>;
    /** Called after the form action completed successfully */
    onSuccess?: () => void;
    /** Index of the form in the forms array (for multiple forms support) */
    formIndex?: number;
    /**
     * Which surface `formIndex` counts in. Left unset, the backend keeps its default of
     * preferring the detail forms on update, which is what a detail page wants; a related list
     * writing through the child's list form has to say so, or the index would land elsewhere.
     */
    formScope?: "list" | "detail";
    /**
     * Values submitted with the form but never shown, e.g. the parent a related list creates
     * into. They still have to be declared in the backend form, which alone decides what is
     * writable.
     */
    fixedValues?: Record<string, any>;
    /**
     * Context parameters the form was opened with (`FormConfig.context_params`, read from the URL
     * by the caller): sent to `form-prefill` and with the write, and read by the conditions.
     */
    context?: Record<string, string>;
};

export function DynamicObjectForm({ open, onOpenChange, mode, formConfig, endpointUrl, recordId, initialValues, onSuccess, formIndex = 0, formScope, fixedValues, context }: DynamicObjectFormProps) {
    const fields = formConfig.fields;
    // `FormConfig.steps`: a creation filled in several steps, each « Suivant » asking `form-step`.
    const steps = mode === "create" && formConfig.name ? (formConfig.steps ?? []) : [];
    const wizard = steps.length > 0;
    // Steps as `form-step` described them, the one on screen last (« Précédent » pops it).
    const [stepStack, setStepStack] = useState<FormStepDescription[]>([]);
    const currentStep = stepStack.length > 0 ? stepStack[stepStack.length - 1] : null;
    /**
     * The scrolling body: every answer of « Suivant » (the next step, or the same one refused with its errors at
     * the top) and every « Précédent » shows the step from its top.
     */
    const bodyScroll = useRef<HTMLDivElement>(null);
    const [scrollRequest, setScrollRequest] = useState(0);
    useEffect(() => {
        bodyScroll.current?.scrollTo?.({ top: 0 });
    }, [scrollRequest]);
    const [stepSummary, setStepSummary] = useState<FormStepSummary | null>(null);
    const [stepGauges, setStepGauges] = useState<FormStepGauge[]>([]);
    // `form-step` answered `complete` after a valid step (every later step skipped): the step on screen is the last one.
    const [stepsComplete, setStepsComplete] = useState(false);
    const [stepping, setStepping] = useState(false);
    // Id of the latest `form-step` request: only its answer is applied (bumped on open and close too).
    const stepRequest = useRef(0);
    const conditional = hasFormConditions(fields) || wizard;
    const followsEditedValues = fields.some(field => field.on_value || field.locked_when?.length);
    const previewOn = formConfig.has_preview && formConfig.name ? (formConfig.preview_on ?? []) : [];
    const contextKey = JSON.stringify(context ?? {});
    const formContext = useMemo(() => context ?? {}, [contextKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // G1: the schema follows the fields the current values leave in the form, so a hidden field is
    // never validated and a conditionally required one is. Read through a ref: the resolver is set once.
    const conditionsRef = useRef<{ context: Record<string, string>; read?: ReturnType<typeof optionAttributeReader>; stepFields?: FieldDataType[] }>({ context: formContext });
    // Every field the values leave in the form: what the write submits.
    const formFieldsFor = (values: Record<string, any>): FieldDataType[] =>
        conditional ? resolveConditionalFields(fields, { ...conditionsRef.current.context, ...values }, conditionsRef.current.read) : fields;
    // What is validated and shown: in a form in steps, the fields of the step on screen only (as served by `form-step`).
    const visibleFieldsFor = (values: Record<string, any>): FieldDataType[] =>
        wizard ? resolveConditionalFields(conditionsRef.current.stepFields ?? [], { ...conditionsRef.current.context, ...values }, conditionsRef.current.read) : formFieldsFor(values);
    const form = useForm<any>({
        resolver: (values, resolverContext, options) => zodResolver(buildZodSchema(visibleFieldsFor(values)))(values, resolverContext, options),
        mode: "onChange",
        defaultValues: buildDefaultValues(fields, mode === "update" ? initialValues : undefined)
    });

    const navigate = useNavigate();
    // Watched only when an option list, a condition or the preview depends on the values being edited.
    const editedValues = readsFormValues(fields) || conditional || previewOn.length > 0 || followsEditedValues ? form.watch() : undefined;
    const { dynamicOptions, loadingOptions, failedOptions, reloadOptions, reloadingOptions } = useDynamicOptions(fields, undefined, editedValues);
    // The descriptors on screen (the step's served ones over the form's) and their effective options
    // (`resolveFieldOptions`: a served list wins over a fetched one): what the fields, the rows that follow a
    // field and the conditions' option attributes all read.
    const descriptors = useMemo(() => currentDescriptors(fields, currentStep?.fields), [fields, currentStep]);
    const fieldOptions = useMemo(() => effectiveOptionMap(descriptors, { dynamicOptions }), [descriptors, dynamicOptions]);
    const readAttribute = useMemo(() => optionAttributeReader(descriptors, fieldOptions), [descriptors, fieldOptions]);
    conditionsRef.current = { context: formContext, read: readAttribute, stepFields: currentStep?.fields };
    const shownFields = wizard ? (currentStep?.fields ?? []) : fields;
    const visibleFields = conditional ? resolveConditionalFields(shownFields, { ...formContext, ...(editedValues ?? {}) }, readAttribute) : fields;
    // A condition reading an option attribute can flip when its list arrives, with no value edited: the
    // resolver runs again then, so « Suivant » / « Valider » follow the fields actually shown.
    const visibleKey = conditional ? visibleFields.map(field => `${field.field_name}:${Boolean(field.required)}`).join(",") : "";
    useEffect(() => {
        if (open && visibleKey) void form.trigger();
    }, [visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const [prefillValues, setPrefillValues] = useState<Record<string, unknown>>({});
    const [prefillAlerts, setPrefillAlerts] = useState<FormAlertType[]>([]);
    const [previewAlerts, setPreviewAlerts] = useState<FormAlertType[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [backendFieldErrors, setBackendFieldErrors] = useState<Record<string, string>>({});
    const [backendErrors, setBackendErrors] = useState<string | null>(null);
    const [duplicate, setDuplicate] = useState<DuplicateRecord | null>(null);

    const title = mode === "create" ? formConfig.create_label : formConfig.update_label;

    // `FormConfig.drafts`: a creation keeps a local draft, per user, form and context (record taken over…).
    const userId = useUser()?.user?.id;
    const draftsEnabled = Boolean(formConfig.drafts) && mode === "create" && open;
    const draftKey = draftsEnabled ? buildFormDraftKey(userId, `${endpointUrl}#${formConfig.name ?? formIndex}`, formContext) : null;
    // The draft is restored once `form-prefill` answered (or failed), so what was typed wins over it.
    const [prefillSettled, setPrefillSettled] = useState(false);
    // Files are never kept in a draft.
    const draftExcluded = useMemo(() => fields.filter(field => field.field_type === "file").map(field => field.field_name), [fields]);
    // `on_value` triggers whose value came back from the draft: their effects are not replayed while
    // they keep that value, otherwise restoring « une personne différente » would blank the identity
    // restored with it. What the user typed wins; a new choice runs the effects again.
    const restoredTriggers = useRef<Record<string, string>>({});
    const draft = useFormDraft({
        storageKey: draftKey,
        form,
        enabled: draftsEnabled,
        ready: prefillSettled,
        exclude: draftExcluded,
        onRestore: values => {
            restoredTriggers.current = Object.fromEntries(
                fields.filter(field => field.on_value && field.field_name in values).map(field => [field.field_name, String(values[field.field_name] ?? "")])
            );
        }
    });

    // Re-sync form values when dialog opens with new initialValues (update mode)
    useEffect(() => {
        if (open) {
            form.reset(buildDefaultValues(fields, mode === "update" ? initialValues : undefined));
            setBackendErrors(null);
            setBackendFieldErrors({});
            setDuplicate(null);
            setPrefillAlerts([]);
            setPreviewAlerts([]);
            setPrefillValues({});
            setPrefillSettled(!(mode === "create" && formConfig.has_prefill && formConfig.name));
            restoredTriggers.current = {};
            resetFieldMemory(form);
            // A `form-step` answer still in flight belongs to the previous opening: ignored.
            stepRequest.current += 1;
            setStepStack([]);
            setStepsComplete(false);
            setStepSummary(null);
            setStepGauges([]);
            setStepping(false);
        } else {
            // Closed: the next opening waits again for its own reset and prefill, and starts with no gauge.
            setPrefillSettled(false);
            stepRequest.current += 1;
            setStepGauges([]);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // G2: initial values of a creation form opened with context parameters (a record taken over).
    useEffect(() => {
        if (!open || mode !== "create" || !formConfig.has_prefill || !formConfig.name) return;
        let cancelled = false;
        const query = new URLSearchParams(formContext).toString();
        DataService.client
            .url(`${endpointUrl}form-prefill/${formConfig.name}/${query ? `?${query}` : ""}`)
            .get()
            .json<FormHandlerPayload>()
            .then(payload => {
                if (cancelled) return;
                // The answer may arrive after the user started typing: what they typed wins.
                Object.entries(payload.values ?? {}).forEach(([name, value]) => {
                    if (form.getFieldState(name).isDirty) return;
                    form.setValue(name, value, { shouldValidate: true });
                });
                setPrefillValues(payload.values ?? {});
                setPrefillAlerts(payload.alerts ?? []);
                setPrefillSettled(true);
            })
            .catch((e: any) => {
                if (cancelled) return;
                let parsed: { error?: string; field_errors?: Record<string, string[]> } | null = null;
                try {
                    parsed = JSON.parse(e?.message);
                } catch {
                    parsed = null;
                }
                const fieldMessages = parsed?.field_errors ? Object.values(parsed.field_errors).flat().join(" ") : "";
                setBackendErrors(fieldMessages || (e?.status === 404 ? "The source record cannot be found or is outside your scope." : parsed?.error || "The form could not be prefilled."));
                setPrefillSettled(true);
            });
        return () => {
            cancelled = true;
        };
    }, [open, formConfig.name, formConfig.has_prefill, endpointUrl, mode, formContext]); // eslint-disable-line react-hooks/exhaustive-deps

    // `on_value`: choosing a value writes the fields it names. `"$prefill"` is what form-prefill
    // put there. Runs on the value, not the widget, so a select and a typed answer do the same thing.
    const onValueKey = fields
        .filter(field => field.on_value)
        .map(field => String(editedValues?.[field.field_name] ?? ""))
        .join("\0");
    useEffect(() => {
        if (!open || !onValueKey) return;
        fields.forEach(field => {
            if (!field.on_value) return;
            const chosen = String(form.getValues(field.field_name) ?? "");
            const restored = restoredTriggers.current[field.field_name];
            if (restored !== undefined) {
                if (restored === chosen) return;
                delete restoredTriggers.current[field.field_name];
            }
            const effects = field.on_value[chosen];
            if (!effects) return;
            Object.entries(effects).forEach(([name, next]) => {
                const value = next === "$prefill" ? (prefillValues[name] ?? "") : next;
                if (form.getValues(name) === value) return;
                form.setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
            });
        });
    }, [open, onValueKey, prefillValues]); // eslint-disable-line react-hooks/exhaustive-deps

    // G3: what the server computes from the values being typed, asked (debounced) when a
    // `preview_on` field changes or appears (its value is then posted). Values it returns are written into the form.
    const previewValuesFor = (values: Record<string, unknown>) => keepVisibleValues(formFieldsFor(values), values);
    const previewKey = previewOn.length > 0 ? previewKeyOf(previewOn, previewValuesFor(editedValues ?? {})) : "";
    useEffect(() => {
        if (!open || !previewKey || !formConfig.name) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            const values = form.getValues();
            DataService.client
                .url(`${endpointUrl}form-preview/${formConfig.name}/`)
                // What the write would submit, every step's (in a form in steps, a preview may read a value of an
                // earlier step: the OPCO chosen with the funding, read with the SIRET typed later).
                .post({ fields: normalizeJsonFieldValues(fields, previewValuesFor(values)) })
                .json<FormHandlerPayload>()
                .then(payload => {
                    if (cancelled) return;
                    Object.entries(payload.values ?? {}).forEach(([name, value]) => form.setValue(name, value, { shouldValidate: true }));
                    setPreviewAlerts(payload.alerts ?? []);
                })
                .catch(() => {
                    // A preview is a courtesy: the write validates on its own.
                    if (!cancelled) setPreviewAlerts([]);
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, previewKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live refresh of a step (`FormStep.live_on`, declared by the server): while the step on screen is
    // filled, a change of one of those fields asks `form-step` with `live: true` what its handler computes
    // from the values typed so far (empty required fields are not errors there), without validating the
    // step nor writing. Every part it returns (`FORM_STEP_LIVE_KEYS`: gauges, summary, the step's alerts) is
    // redrawn; a part left out, or values the server refuses (`{}`), leave what is shown.
    const liveOn = wizard && currentStep ? (steps.find(step => step.key === currentStep.key)?.live_on ?? []) : [];
    const stepDescription = wizard && currentStep ? steps.find(step => step.key === currentStep.key)?.description : null;
    const liveStepKey = liveOn.length > 0 ? currentStep?.key ?? null : null;
    const liveKey = liveStepKey ? JSON.stringify([liveStepKey, ...liveOn.map(name => (editedValues as Record<string, unknown> | undefined)?.[name] ?? null)]) : "";
    // Id of the latest live request: only its answer is applied. Bumped by every cleanup of the effect below
    // (values or step changed, « Précédent », a step without `live_on`, close, unmount) and by « Suivant ».
    const liveRequest = useRef(0);
    useEffect(() => {
        if (!open || !liveStepKey || !formConfig.name) return;
        const id = ++liveRequest.current;
        const timer = setTimeout(() => {
            DataService.client
                .url(`${endpointUrl}form-step/${formConfig.name}/`)
                .post({
                    step: liveStepKey,
                    live: true,
                    fields: { ...normalizeJsonFieldValues(fields, form.getValues()), ...fixedValues },
                    ...(formConfig.context_params?.length ? { context: formContext } : {})
                })
                .json<FormStepLiveResponse>()
                .then(response => {
                    if (id !== liveRequest.current) return;
                    if (response.gauges) setStepGauges(response.gauges);
                    if (response.summary) setStepSummary(response.summary);
                    const alerts = response.alerts;
                    if (alerts) setStepStack(stack => stack.map((item, index) => (index === stack.length - 1 && item.key === liveStepKey ? { ...item, alerts } : item)));
                })
                .catch(() => {
                    // A courtesy: « Suivant » computes it again.
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
            // An answer still in flight belongs to values, a step or an opening no longer on screen.
            liveRequest.current += 1;
        };
    }, [open, liveKey]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Posts the values to `form-step`, leaving `stepKey` (`null`: the first step). */
    const askStep = async (stepKey: string | null) => {
        liveRequest.current += 1;
        setBackendErrors(null);
        setBackendFieldErrors({});
        setStepping(true);
        const id = ++stepRequest.current;
        const stale = () => id !== stepRequest.current;
        try {
            const response = await DataService.client
                .url(`${endpointUrl}form-step/${formConfig.name}/`)
                .post({
                    step: stepKey,
                    fields: { ...normalizeJsonFieldValues(fields, form.getValues()), ...fixedValues },
                    ...(formConfig.context_params?.length ? { context: formContext } : {})
                })
                .json<FormStepResponse>();
            if (stale()) return;
            Object.entries(response.values ?? {}).forEach(([name, value]) => form.setValue(name, formValueOf(value, form.getValues(name)), { shouldValidate: true }));
            if (response.summary) setStepSummary(response.summary);
            // Gauges come back whenever the step handler ran, a step it rejects included; a step rejected
            // before it (generic validation) comes back without them: keep those shown.
            if (response.gauges) setStepGauges(response.gauges);
            const described = response.step;
            if (described) setStepStack(stack => [...stack.filter(item => item.index < described.index), described]);
            setScrollRequest(request => request + 1);
            setStepsComplete(response.valid && stepKey !== null && (response.complete || !described));
            if (!response.valid) {
                setBackendFieldErrors(Object.fromEntries(Object.entries(response.field_errors).map(([field, messages]) => [field, messages.join(" ")])));
                // Errors on fields of another step (a draft, a value changed elsewhere): shown above the fields.
                // An error on a row of a fieldset (`dates.1.start_date`) is shown on that row.
                const elsewhere = errorsElsewhere(response.field_errors, described?.fields ?? []);
                if (elsewhere.length > 0) setBackendErrors(elsewhere.flatMap(([, messages]) => messages).join(" "));
            }
        } catch (e: any) {
            if (stale()) return;
            let parsed: { error?: string } | null = null;
            try {
                parsed = JSON.parse(e?.message);
            } catch {
                parsed = null;
            }
            setBackendErrors(parsed?.error || "Could not move to the next step.");
            bugsnagNotify("DynamicObjectForm > askStep()", { endpointUrl, stepKey }, e);
        } finally {
            if (!stale()) setStepping(false);
        }
    };

    // The first step, once the prefill (and the draft restored after it) settled.
    useEffect(() => {
        if (!open || !wizard || !prefillSettled || stepStack.length > 0) return;
        askStep(null);
    }, [open, wizard, prefillSettled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Another step on screen: validate again against its fields (the resolver reads them through the ref),
    // so « Suivant » and its errors follow the step shown, not the one left. Touched and submitted states
    // are dropped so the new step's empty fields are not flagged before they are used.
    useEffect(() => {
        if (!wizard || !currentStep) return;
        form.reset(form.getValues(), { keepValues: true, keepDirty: true, keepDirtyValues: true, keepDefaultValues: true });
        void form.trigger();
    }, [wizard, currentStep?.key, currentStep?.index]); // eslint-disable-line react-hooks/exhaustive-deps

    /** « Précédent », or a step of the stepper already reached: no request, the step as it was described. */
    const goBackTo = (index: number) => {
        // A « Suivant » still in flight (the stepper stays clickable) would bring the later step back: ignored,
        // and so is a live refresh of the step left.
        stepRequest.current += 1;
        liveRequest.current += 1;
        setStepping(false);
        setBackendErrors(null);
        setBackendFieldErrors({});
        setStepsComplete(false);
        setStepStack(stack => stack.filter(item => item.index <= index));
        setScrollRequest(request => request + 1);
    };
    const isLastStep = wizard && currentStep !== null && (stepsComplete || currentStep.index === steps.length - 1);
    // The last step with no field of its own (« Récapitulatif »): the summary laid out in full, nothing beside it.
    const reviewing = isLastStep && visibleFields.length === 0;
    // « Edit » on a section titled as a step already reached goes back to that step.
    const editStep = (section: { title: string; step?: string | null }) => {
        const target = steps.findIndex(step => (section.step ? step.key === section.step : step.label === section.title));
        if (target < 0 || target === currentStep?.index || !stepStack.some(item => item.index === target)) return null;
        return () => goBackTo(target);
    };

    const onSubmit = async (values: any) => {
        if (wizard && !isLastStep) {
            if (currentStep) await askStep(currentStep.key);
            return;
        }
        setBackendErrors(null);
        setBackendFieldErrors({});
        setDuplicate(null);
        setSubmitting(true);
        try {
            // In steps, the resolver only returns the values of the step on screen: the write takes them all.
            const written = wizard ? form.getValues() : values;
            // A field hidden by its conditions is not submitted (`form-action` would drop it anyway).
            const submitted = conditional ? keepVisibleValues(formFieldsFor(written), written) : written;
            const payloadValues = normalizeJsonFieldValues(fields, submitted);
            const response = await DataService.client
                .url(endpointUrl + "form-action/")
                .post({
                    action_type: mode === "create" ? "__create__" : "__update__",
                    id: mode === "update" ? recordId : undefined,
                    form_index: formIndex,
                    form_scope: formScope,
                    fields: { ...payloadValues, ...fixedValues },
                    ...(formConfig.context_params?.length ? { context: formContext } : {})
                })
                .json<{
                    success: boolean;
                    id?: number;
                    error?: string;
                    missing_fields?: string[];
                    field_errors?: Record<string, string[]>;
                    redirect_url?: string | null;
                }>();

            if (response && response.success) {
                toast.success(mode === "create" ? "Record created" : "Record updated");
                draft.clear();
                onSuccess?.();
                onOpenChange(false);
                form.reset();
                // `FormConfig.redirect_after_create`, resolved server-side against the record
                // just created. Same convention as a dynamic action's `redirect_url`: it lets
                // an object whose creation is only the first step of something — a workflow
                // that still has to be drawn — hand the operator to that step instead of
                // dropping them back on the list.
                if (response.redirect_url) navigate(response.redirect_url);
            } else {
                toast.error(response?.error || "The operation failed");
                const fieldErrors: Record<string, string> = {};
                response?.missing_fields?.forEach((field: string) => {
                    fieldErrors[field] = "Ce champ est requis";
                });
                Object.entries(response?.field_errors ?? {}).forEach(([field, messages]) => {
                    fieldErrors[field] = messages.join(" ");
                });
                if (Object.keys(fieldErrors).length > 0) {
                    setBackendFieldErrors(fieldErrors);
                    // In a form in steps, a field not on screen (the review step shows none) has its error above.
                    const elsewhere = wizard ? errorsElsewhere(fieldErrors, visibleFields) : [];
                    if (elsewhere.length > 0) setBackendErrors(elsewhere.map(([, message]) => message).join(" "));
                } else {
                    setBackendErrors(response?.error || "Unknown error");
                }
            }
        } catch (e: any) {
            let parsed: ValidationErrorPayload | null = null;
            try {
                parsed = JSON.parse(e?.message);
            } catch {
                parsed = null;
            }
            // A validator rejection is an expected outcome of a form, not an incident: show it on
            // the fields it belongs to and keep it out of Bugsnag.
            const fieldErrors = parsed?.field_errors;
            if (fieldErrors) {
                setBackendFieldErrors(Object.fromEntries(Object.entries(fieldErrors).map(([field, messages]) => [field, messages[0]])));
                // In a form in steps, a field of an earlier step is not on screen: its error is shown above.
                const elsewhere = wizard ? errorsElsewhere(fieldErrors as Record<string, string[]>, visibleFields) : [];
                if (elsewhere.length > 0) setBackendErrors(elsewhere.flatMap(([, messages]) => messages).join(" "));
            }
            if (parsed?.duplicate) {
                setDuplicate(parsed.duplicate);
            }
            if (fieldErrors || parsed?.duplicate) {
                toast.error(parsed?.duplicate ? "This record already exists" : "Please fix the fields flagged");
            } else {
                const message = errorText(parsed?.error);
                toast.error(message || "The operation failed");
                setBackendErrors(message || e.toString());
                bugsnagNotify("DynamicObjectForm > onSubmit()", { endpointUrl, recordId, initialValues, formIndex }, e);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* The title, the steps and the buttons stay in place: only the body scrolls. */}
            <DialogContent className={wizard ? dialogClass(formConfig.size ?? "xl", "overflow-hidden") : "flex max-h-[90vh] max-w-2xl flex-col overflow-hidden"}>
                <DialogHeader className="shrink-0">
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                {wizard && (
                    <div className="shrink-0">
                        <FormStepper steps={steps} current={currentStep?.index ?? 0} reached={stepStack.map(item => item.index)} onGoTo={goBackTo} />
                    </div>
                )}

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                        <div ref={bodyScroll} className="relative -m-1 min-h-0 flex-1 overflow-y-auto p-1" data-testid="form-body-scroll">
                        {backendErrors && <FormError errorMessage={backendErrors} />}
                        {duplicate && (
                            <Alert variant="warning">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertTitle>This record already exists</AlertTitle>
                                <AlertDescription>
                                    {duplicate.url ? (
                                        <>
                                            Sa fiche est disponible en cliquant sur{" "}
                                            <NavLink to={duplicate.url} className="underline" target="_blank">
                                                ce lien
                                            </NavLink>
                                            .
                                        </>
                                    ) : (
                                        duplicate.label
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}
                        {/* In steps, on the first one only: further on, the steps already reached are the draft's. */}
                        {(!wizard || stepStack.length <= 1) && <FormDraftNotice restoredAt={draft.restoredAt} onDiscard={draft.discard} />}
                        {(() => {
                            // In steps, what the prefill and the live preview say while typing goes to the summary
                            // column (`asideAlerts`): the fields never move under the cursor. The step's own alerts
                            // (answered by « Next ») stay above.
                            const above = wizard && !reviewing ? (currentStep?.alerts ?? []) : [...prefillAlerts, ...previewAlerts, ...(currentStep?.alerts ?? [])];
                            return above.length > 0 ? <AlertLayout embedded alerts={above} /> : null;
                        })()}
                        {reviewing ? (
                            <FormSummaryReview summary={stepSummary} gauges={stepGauges} editStep={editStep} />
                        ) : (
                        <div className={wizard ? "grid gap-4 md:grid-cols-[minmax(0,1fr)_320px]" : undefined}>
                        <div className="grid grid-cols-2 content-start gap-4 py-4">
                            {stepDescription && <p className="col-span-2 text-sm text-gray-600">{stepDescription}</p>}
                            {fieldRuns(visibleFields).map((run, index) => {
                                const props = {
                                    form,
                                    dynamicOptions: fieldOptions,
                                    loadingOptions,
                                    failedOptions,
                                    backendFieldErrors,
                                    contextName: "object-form",
                                    readAttribute,
                                    // The conditions read the form's values: a multiple choice among them item by item.
                                    siblingFields: descriptors,
                                    prefillValues,
                                    onReloadOptions: formConfig.refreshable_options ? reloadOptions : undefined,
                                    reloadingOptions
                                };
                                // Fields the descriptor groups (`Field.field_group`): one framed block, titled.
                                return run.group ? (
                                    <FieldGroupBlock key={`group:${run.group.key}:${index}`} group={run.group} fields={run.fields} {...props} />
                                ) : (
                                    run.fields.map(field => <DynamicFormField key={field.field_name} field={field} {...props} />)
                                );
                            })}
                            {isLastStep && <p className="col-span-2 text-sm text-gray-600">Review the summary, then create.</p>}
                        </div>
                        {wizard && (
                            // The summary fills as the steps go: it scrolls in its column (the height of the fields, at
                            // least `min-h`) rather than growing the dialog and pushing the buttons down.
                            <div className="relative mt-4 md:min-h-[24rem]">
                                <div className="space-y-3 md:absolute md:inset-0 md:overflow-y-auto" data-testid="form-summary-scroll">
                                    {prefillAlerts.length + previewAlerts.length > 0 && <AlertLayout embedded compact alerts={[...prefillAlerts, ...previewAlerts]} />}
                                    <FormSummaryPanel summary={stepSummary} gauges={stepGauges} nextSteps={steps.slice((currentStep?.index ?? 0) + 1).map(step => step.label)} />
                                </div>
                            </div>
                        )}
                        </div>
                        )}

                        </div>
                        <div className="mt-4 flex shrink-0 justify-end gap-2 border-t pt-4">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            {wizard ? (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={stepStack.length < 2 || stepping || submitting}
                                        onClick={() => goBackTo(stepStack[stepStack.length - 2]?.index ?? 0)}
                                    >
                                        Back
                                    </Button>
                                    <Button type="submit" variant="default" disabled={!currentStep || !form.formState.isValid || stepping || submitting}>
                                        {stepping || submitting ? "Saving…" : isLastStep ? "Create" : "Next"}
                                    </Button>
                                </>
                            ) : (
                                <Button type="submit" variant="default" disabled={!form.formState.isValid || submitting}>
                                    {submitting ? "Saving…" : form.formState.isValid ? (mode === "create" ? "Create" : "Save") : "Fill in the required fields"}
                                </Button>
                            )}
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
